import L from "leaflet";
import React, { useEffect, useMemo, useRef } from "react";
import { GeoJSON, useMap } from "react-leaflet";
import { coreGet } from "@/services/coreApi";
import { bindMapCard, useCloseMapCardWhenHidden } from "@/features/map/mapCardStore";
import { NAV_CAT_MAP } from "@/config/defaults";
import { ensurePane } from "@/utils/mapUx";
import {
  computeClosedViaAreaFlags,
  computeLegLengths,
  legPopupProperties,
  selectVisibleLegFeatures,
} from "./rnwLegColumns";

function getUsageStyle(feature) {
  const usage = feature.properties.usage;
  const color =
    usage === 0
      ? "orange"
      : usage === 2
        ? "red"
        : usage === 3
          ? "blue"
          : usage === 4
            ? "green"
            : "black";
  return { color, weight: 2, fillColor: color };
}

function getNavigabilityStyle(feature) {
  const navValue = feature.properties.ww_nav_cat;
  const color =
    navValue === 1
      ? "green"
      : navValue === 2
        ? "lightgreen"
        : navValue === 3
          ? "blue"
          : navValue === 4
            ? "orange"
            : navValue === 5
              ? "red"
              : "black";
  return { color, weight: 2, fillColor: color };
}

function getVertClearanceStyle(feature) {
  const color =
    feature.properties.ww_is_free_vessel_vertical_clearance === false
      ? "red"
      : "green";
  return { color, weight: 2, fillColor: color };
}

function getUsabilityStyle(feature) {
  const color = feature.properties.ww_legUsable === false ? "red" : "green";
  return { color, weight: 2, fillColor: color };
}

function getViaareasStyle(feature) {
  const color =
    feature.properties.ww_is_closed_viaarea === true ? "red" : "green";
  return { color, weight: 2, fillColor: color };
}

const VIEWPORT_PAD = 0.25;
// Above benchmap's weather pane (450), below seamarks (470), GIS (480) and routes (600+).
const RNW_PANE = "rnwLegsPane";
const RNW_PANE_Z = 460;

function SimpleArrow({ positions, style }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const decorator = L.polylineDecorator(positions, {
      patterns: [
        {
          offset: "50%",
          repeat: 0,
          symbol: L.Symbol.arrowHead({
            pixelSize: 15,
            polygon: false,
            pathOptions: {
              pane: RNW_PANE,
              stroke: true,
              color: style.color,
              weight: style.weight,
            },
          }),
        },
      ],
    }).addTo(map);

    return () => {
      map.removeLayer(decorator);
    };
  }, [map, positions, style]);

  return null;
}

function RnwGeojsonOverlay({
  rnwLegs,
  cwLegsToViaAreasGeojson,
  setCwLegsToViaAreasGeojson,
  cwViaAreasGeojson,
  zoomLevel,
  dragged,
  resized,
  geojsonVersion,
  selectedMetrics,
  rnwUiDisabled,
  plannerVesselSettings,
  switchRnwAnalyserActive,
  onLayerRendered,
  accessContext,
  setAccessContext,
}) {
  const map = useMap();
  // Canvas instead of SVG: thousands of legs as DOM paths make pan/zoom sluggish.
  // Fresh renderer per show/data cycle: a removed L.canvas loses its context
  // and stays blank on re-add until the map moves.
  const canvasRenderer = useMemo(() => {
    ensurePane(map, RNW_PANE, RNW_PANE_Z);
    return L.canvas({ padding: VIEWPORT_PAD, pane: RNW_PANE, tolerance: 6 });
  }, [map, switchRnwAnalyserActive, rnwLegs]);

  useEffect(
    () => () => {
      if (map.hasLayer(canvasRenderer)) map.removeLayer(canvasRenderer);
    },
    [map, canvasRenderer],
  );

  const accessRef = useRef({ accessContext, setAccessContext });
  accessRef.current = { accessContext, setAccessContext };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await coreGet(
          "/geo/legs-to-via-areas",
          accessRef.current.accessContext,
          accessRef.current.setAccessContext,
          ".core /geo/legs-to-via-areas error=",
        );
        setCwLegsToViaAreasGeojson(data);
      } catch (error) {
        console.error(error);
      }
    };
    load();
  }, [setCwLegsToViaAreasGeojson]);

  // Memoised — a fresh array identity here would invalidate the (expensive)
  // closedViaAreaFlags memo on every render.
  const viaAreaIdsNotOpen = useMemo(
    () =>
      cwViaAreasGeojson?.features
        ?.filter((feature) => feature.properties.open === false)
        .map((feature) => feature.properties.id),
    [cwViaAreasGeojson],
  );

  const onEachFeature = (feature, layer) => {
    const originalWeight = layer.options.weight;
    bindMapCard(layer, () => {
      layer.setStyle({ weight: originalWeight * 3 });
      const legIndex = feature.properties.legIndex;
      return {
        key: `rnw-leg-${legIndex}`,
        title: "RNW leg",
        rows: Object.entries(legPopupProperties(rnwLegs, legIndex, closedViaAreaFlags)),
        onClose: () => layer.setStyle({ weight: originalWeight }),
      };
    });
  };

  const styleFunction = (feature) => {
    if (rnwUiDisabled) {
      return { color: "green", fillColor: "green", weight: 2 };
    }

    const metricChecks = {
      Navigability:
        feature.properties.ww_nav_cat <=
        (NAV_CAT_MAP[plannerVesselSettings.navigabilityCategory] ?? 1),
      airDraft: feature.properties.ww_is_free_vessel_vertical_clearance,
      Usability: feature.properties.ww_legUsable !== false,
      ViaAreas: !feature.properties.ww_is_closed_viaarea,
    };

    const activeMetrics = Object.keys(selectedMetrics).filter(
      (metric) => selectedMetrics[metric],
    );

    if (activeMetrics.length === 0) return undefined;
    if (activeMetrics.length === 1) {
      const metric = activeMetrics[0];
      if (metric === "Usage") return getUsageStyle(feature);
      if (metric === "Navigability") return getNavigabilityStyle(feature);
      if (metric === "airDraft") return getVertClearanceStyle(feature);
      if (metric === "Usability") return getUsabilityStyle(feature);
      if (metric === "ViaAreas") return getViaareasStyle(feature);
    }

    const isRed = activeMetrics.some((metric) => !metricChecks[metric]);
    const color = isRed ? "red" : "green";
    return { color, fillColor: color, weight: 2 };
  };

  useCloseMapCardWhenHidden("rnw-leg-", !!rnwLegs);

  const legLengths = useMemo(
    () => (rnwLegs ? computeLegLengths(rnwLegs) : null),
    [rnwLegs],
  );

  const closedViaAreaFlags = useMemo(() => {
    if (!rnwLegs || !cwLegsToViaAreasGeojson) return null;
    const links =
      cwLegsToViaAreasGeojson?.cwGeojsonData?.nodes[0]?.data?.features || [];
    return computeClosedViaAreaFlags(rnwLegs, links, viaAreaIdsNotOpen);
  }, [rnwLegs, cwLegsToViaAreasGeojson, viaAreaIdsNotOpen]);

  // Only redo when the viewport (zoom/drag/resize) or the data changed.
  const visibleFeatures = useMemo(() => {
    if (!legLengths || !closedViaAreaFlags) return null;
    const bounds = map.getBounds().pad(VIEWPORT_PAD);
    return selectVisibleLegFeatures(
      rnwLegs,
      legLengths,
      closedViaAreaFlags,
      {
        south: bounds.getSouth(),
        west: bounds.getWest(),
        north: bounds.getNorth(),
        east: bounds.getEast(),
      },
      map.getZoom(),
    );
  }, [map, rnwLegs, legLengths, closedViaAreaFlags, zoomLevel, dragged, resized]);

  if (!switchRnwAnalyserActive || !visibleFeatures) return null;

  return (
    <>
      <GeoJSON
        key={`rnw-geojson-${zoomLevel}-${dragged}-${resized}-${geojsonVersion}-${viaAreaIdsNotOpen}`}
        interactive
        transparent
        renderer={canvasRenderer}
        data={visibleFeatures}
        style={styleFunction}
        onEachFeature={onEachFeature}
        eventHandlers={{
          // Signals "the layer is actually on the map" back to the run
          // spinner; rAF waits for the paint after leaflet added the layer.
          add: () => {
            requestAnimationFrame(() => onLayerRendered?.());
          },
        }}
      />
      {selectedMetrics.Usage &&
        zoomLevel > 11 &&
        visibleFeatures.map((feature) => {
          if (
            feature.geometry.type === "LineString" &&
            feature.properties.usage === 0
          ) {
            const latlngs = feature.geometry.coordinates.map(([lon, lat]) => [
              lat,
              lon,
            ]);
            const style = getUsageStyle(feature);

            return (
              <SimpleArrow
                key={`usage-arrow-${feature.properties.id}`}
                positions={latlngs}
                style={style}
              />
            );
          }
          return null;
        })}
    </>
  );
}

// Memoised — the overlay does heavy per-feature work, so panel-local state
// ticks (specs typing, view switches) must not re-render it.
export default React.memo(RnwGeojsonOverlay);
